import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { isAxiosError } from 'axios';
import { authApiV2 } from '@/api/endpoints/auth';
import { useLogin } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth.store';
import { clearExplicitLogout } from '@/features/auth/lib/explicit-logout';
import { parkImpersonationTarget } from '@/features/auth/lib/impersonation-target';
import { isInternalEmail } from '@/features/auth/lib/internal-domains';
import { AuthMarketingShell } from '@/features/auth/components/AuthMarketingShell';
import { AuthTextField } from '@/features/auth/components/AuthTextField';
import { PreLogGuestRedirect } from '@/features/auth/components/PreLogGuestRedirect';
import { DvClaimPanel } from '@/features/auth/components/DvClaimPanel';
import { DevAuthPersonaBar } from '@/features/auth/components/DevAuthPersonaBar';
import { isDevAuth } from '@/lib/dev-auth';
import { isLocalSupabaseUrl, probeAzureSsoReady } from '@/lib/azure-sso';
import { getSupabaseClient, isSupabaseAuthEnabled } from '@/lib/supabase';
import { cn } from '@/lib/cn';
import {
  DEFAULT_POST_AUTH_PATH,
  normalizePostAuthPath,
  pathWithSearch,
} from '@/lib/default-landing';

/** Server codes that mean password login is blocked — hand off to Microsoft SSO. */
const SSI_LOGIN_REQUIRED_CODES = new Set(['use_ssi_login', 'wrong_auth_provider']);

const loginSchema = z.object({
  email: z.email('Please enter a valid email address'),
  // Blank password is allowed: SSI-domain Sign-in goes to Microsoft; non-SSI
  // blank password is rejected in onSubmit. Impersonation uses SSI Login with
  // a non-SSI email + blank password (PRD §11/07).
  password: z.string(),
});

type LoginFormData = z.infer<typeof loginSchema>;

type LoginErrorBody = {
  error?: { code?: string; message?: string; details?: Record<string, unknown> };
  code?: string;
  message?: string;
};

type AzureOAuthOptions = {
  /** Pre-fill Microsoft login for SSI self-login. Never set for impersonation. */
  loginHint?: string | null;
};

/**
 * Resolve post-login destination from deep-link `returnTo` query, router
 * location state, or the dashboard default.
 */
function resolveReturnTo(
  searchParams: URLSearchParams,
  locationState: { from?: { pathname?: string; search?: string; hash?: string } } | null,
): string {
  const fromQuery = searchParams.get('returnTo');
  if (fromQuery && fromQuery.startsWith('/') && !fromQuery.startsWith('//')) {
    return normalizePostAuthPath(fromQuery);
  }
  // DEF-410 — preserve `?tab=` / `?reportId=` from the ProtectedRoute bounce.
  const fromState = pathWithSearch(locationState?.from ?? null);
  if (fromState) {
    return normalizePostAuthPath(fromState);
  }
  return DEFAULT_POST_AUTH_PATH;
}

/** Extract V3 (or legacy envelope) error code from a login failure. */
function loginErrorCode(error: unknown): string | undefined {
  if (!isAxiosError(error)) return undefined;
  const data = error.response?.data as LoginErrorBody | undefined;
  return data?.error?.code ?? data?.code;
}

function loginErrorDetails(error: unknown): Record<string, unknown> | undefined {
  if (!isAxiosError(error)) return undefined;
  const data = error.response?.data as LoginErrorBody | undefined;
  const details = data?.error?.details;
  return details && typeof details === 'object' ? details : undefined;
}

/** True when the server rejected password login in favor of Microsoft / SSI Login. */
function shouldAutoStartSsiLogin(error: unknown): boolean {
  const code = loginErrorCode(error);
  return code !== undefined && SSI_LOGIN_REQUIRED_CODES.has(code);
}

/**
 * Map V3 `{ error: { code } }` (and legacy envelope) onto PRD login copy.
 */
function loginErrorMessage(error: unknown): string {
  if (!isAxiosError(error)) {
    return 'Email or password is incorrect.';
  }
  const data = error.response?.data as LoginErrorBody | undefined;
  const code = data?.error?.code ?? data?.code;
  const message = data?.error?.message ?? data?.message;
  const status = error.response?.status;
  if (code === 'access_revoked') {
    return 'You no longer have access to TruSpace. Please contact your administrator if you feel this is a mistake.';
  }
  if (code === 'locked') {
    return 'Your account is locked. Try again later or reset your password.';
  }
  if (code === 'account_disabled') {
    return 'Account is disabled. Please contact your administrator.';
  }
  if (code === 'wrong_auth_provider' || code === 'use_ssi_login') {
    return 'This account must sign in with Microsoft SSO.';
  }
  if (code === 'pending_approval') {
    return 'Account pending approval';
  }
  if (code === 'rate_limited' || status === 429) {
    return 'Too many sign-in attempts. Please wait a few minutes and try again.';
  }
  return message ?? 'Email or password is incorrect.';
}

export function LoginPage() {
  const loginMutation = useLogin();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading } = useAuthStore();
  const supabaseConfigured = isSupabaseAuthEnabled();
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const [azureSsoReady, setAzureSsoReady] = useState<boolean | null>(null);
  /** Active SSI domains from `GET /api/auth/internal-domains` (`null` = loading). */
  const [internalDomains, setInternalDomains] = useState<string[] | null>(null);
  const [ssoClickError, setSsoClickError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  /** True while OAuth redirect is in flight (before leaving the page). */
  const [isSsoRedirecting, setIsSsoRedirecting] = useState(false);
  /** True when Sign-in is handing off to Microsoft (suppresses the red banner). */
  const [isAutoSsiRedirect, setIsAutoSsiRedirect] = useState(false);
  /**
   * US-DVU-18 — claim panel only when the server returns explicit `claim_required`.
   * Never swap on generic `account_disabled` / 401 / 403.
   */
  const [claimEmail, setClaimEmail] = useState<string | null>(null);
  const [claimSuggestedNames, setClaimSuggestedNames] = useState<{
    firstName?: string;
    lastName?: string;
  } | null>(null);

  useEffect(() => {
    if (searchParams.get('claim') !== '1') return;
    const fromQuery = searchParams.get('email')?.trim() ?? '';
    if (fromQuery.length > 0) setClaimEmail(fromQuery);
  }, [searchParams]);

  const redirectTo = resolveReturnTo(
    searchParams,
    location.state as { from?: { pathname?: string } } | null,
  );

  useEffect(() => {
    if (!supabaseConfigured || !supabaseUrl) {
      setAzureSsoReady(null);
      return;
    }
    let cancelled = false;
    void probeAzureSsoReady(supabaseUrl).then((ready) => {
      if (!cancelled) setAzureSsoReady(ready);
    });
    return () => {
      cancelled = true;
    };
  }, [supabaseConfigured, supabaseUrl]);

  // Load SSI domain allowlist for Sign-in routing + impersonation parking gates.
  useEffect(() => {
    let cancelled = false;
    void authApiV2
      .getInternalDomains()
      .then((res) => {
        if (!cancelled) {
          setInternalDomains(
            (res.domains ?? []).map((d) => d.trim().toLowerCase()).filter(Boolean),
          );
        }
      })
      .catch(() => {
        // Fail open to password login + server `use_ssi_login` fallback.
        if (!cancelled) setInternalDomains([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });
  const emailValue = watch('email') ?? '';
  const passwordValue = watch('password') ?? '';

  // Drop stale banners as soon as the user edits credentials — never stack
  // a leftover field/SSO message with a live login error.
  useEffect(() => {
    const subscription = watch((_value, { name }) => {
      if (name !== 'email' && name !== 'password') return;
      setSsoClickError(null);
      setIsAutoSsiRedirect(false);
      clearErrors('password');
      loginMutation.reset();
    });
    return () => subscription.unsubscribe();
    // loginMutation.reset is stable in TanStack Query; omit the whole mutation object.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only
  }, [watch, clearErrors]);

  // Real sessions skip the login screen; dev-auth keeps this page reachable
  // so Microsoft SSO can be exercised without disabling VITE_DEV_AUTH.
  if (isAuthenticated && !isDevAuth()) {
    return <PreLogGuestRedirect to={redirectTo} />;
  }
  if (isDevAuth() && isLoading) {
    return <AuthMarketingShell isLoading />;
  }

  /**
   * Start Azure/Entra OAuth.
   * @returns false when SSO cannot start (caller may surface a fallback error).
   */
  function startAzureOAuth(options: AzureOAuthOptions = {}): boolean {
    if (!supabaseConfigured) {
      setIsSsoRedirecting(false);
      setIsAutoSsiRedirect(false);
      setSsoClickError(
        'Microsoft SSO is not configured. Contact your administrator.',
      );
      return false;
    }
    if (azureSsoReady === false) {
      setIsSsoRedirecting(false);
      setIsAutoSsiRedirect(false);
      setSsoClickError(
        isLocalSupabaseUrl(supabaseUrl)
          ? 'Microsoft SSO is not configured for local Supabase. Add Entra credentials to the project-root .env (see .env.example), run supabase stop && supabase start, or switch truspace-web/.env.local to the STAGE Supabase block.'
          : 'Microsoft SSO is not enabled on this Supabase project. Enable the Azure provider in the Supabase dashboard.',
      );
      return false;
    }
    // Intentional new login. Drop the company fromLogout marker so the
    // Microsoft session is accepted on the first SSI Login click (FR-330 bounce).
    clearExplicitLogout();
    const callback = new URL(`${window.location.origin}/v2/auth/sso/callback`);
    callback.searchParams.set('returnTo', redirectTo);
    setIsSsoRedirecting(true);

    const loginHint = options.loginHint?.trim().toLowerCase() ?? '';
    const queryParams =
      loginHint.includes('@') ? { login_hint: loginHint } : undefined;

    // Full-page navigation is required for OAuth. Do not rely on supabase-js
    // auto-redirect alone — DevTools often shows probe/fetch cancels that look
    // like a failed SSI login when the real issue was never leaving /v2/login.
    void getSupabaseClient()
      .auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'openid email profile',
          redirectTo: callback.toString(),
          skipBrowserRedirect: true,
          ...(queryParams ? { queryParams } : {}),
        },
      })
      .then(({ data, error }) => {
        if (error || !data.url) {
          setIsSsoRedirecting(false);
          setIsAutoSsiRedirect(false);
          setSsoClickError(error?.message ?? 'Unable to start Microsoft sign-in.');
          return;
        }
        window.location.assign(data.url);
      })
      .catch(() => {
        setIsSsoRedirecting(false);
        setIsAutoSsiRedirect(false);
        setSsoClickError('Unable to continue. Please try again.');
      });
    return true;
  }

  /** SSI-domain self-login: Microsoft with login_hint, never park impersonation. */
  function startSsiSelfLogin(email: string): void {
    const normalized = email.trim().toLowerCase();
    parkImpersonationTarget(null);
    setIsAutoSsiRedirect(true);
    startAzureOAuth({ loginHint: normalized });
  }

  function onSubmit(data: LoginFormData) {
    setSsoClickError(null);
    setIsAutoSsiRedirect(false);
    const email = data.email.trim().toLowerCase();
    const blankPassword = !data.password || data.password.length === 0;

    // Domain-first: SSI internal email → Microsoft (blank or filled password).
    if (isInternalEmail(email, internalDomains)) {
      clearErrors('password');
      startSsiSelfLogin(email);
      return;
    }

    if (blankPassword) {
      setError('password', {
        type: 'manual',
        message: 'Password is required.',
      });
      return;
    }
    clearErrors('password');
    parkImpersonationTarget(null);
    loginMutation.mutate(
      {
        email: data.email,
        password: data.password,
        returnTo: redirectTo,
      },
      {
        onError: (error) => {
          // Unregistered self-serve-eligible invitee — hand off to register
          // with the email pre-filled (login-side mirror of claim_required).
          if (loginErrorCode(error) === 'registration_required') {
            loginMutation.reset();
            navigate(`/register?email=${encodeURIComponent(email)}`);
            return;
          }
          if (loginErrorCode(error) === 'claim_required') {
            const details = loginErrorDetails(error);
            setClaimSuggestedNames({
              firstName:
                typeof details?.suggestedFirstName === 'string'
                  ? details.suggestedFirstName
                  : undefined,
              lastName:
                typeof details?.suggestedLastName === 'string'
                  ? details.suggestedLastName
                  : undefined,
            });
            setClaimEmail(email);
            loginMutation.reset();
            return;
          }
          // Safety net when the domain probe missed an SSI/sso_only account.
          if (!shouldAutoStartSsiLogin(error)) return;
          setIsAutoSsiRedirect(true);
          parkImpersonationTarget(null);
          loginMutation.reset();
          startAzureOAuth({ loginHint: email });
        },
      },
    );
  }

  function handleTogglePasswordVisibility() {
    setShowPassword((visible) => !visible);
  }

  function handleClearEmail() {
    setValue('email', '', { shouldDirty: true, shouldValidate: true });
  }

  function handleClearPassword() {
    setValue('password', '', { shouldDirty: true, shouldValidate: true });
  }

  function onSsiLoginClick() {
    setSsoClickError(null);
    setIsAutoSsiRedirect(false);
    const values = getValues();
    const email = values.email?.trim().toLowerCase() ?? '';
    const hasEmail = email.includes('@');
    const blankPassword = !values.password || values.password.length === 0;
    const isSsiDomain = hasEmail && isInternalEmail(email, internalDomains);

    // SSI domain (any password) → self Microsoft login with login_hint.
    if (isSsiDomain) {
      startSsiSelfLogin(email);
      return;
    }

    // No email → Microsoft as the browser's Entra session (no hint, no park).
    if (!hasEmail) {
      parkImpersonationTarget(null);
      startAzureOAuth();
      return;
    }

    // Non-SSI email + filled password: not impersonation, not SSI self-login.
    // Keep copy generic so the undiscoverable flow stays undiscoverable.
    if (!blankPassword) {
      parkImpersonationTarget(null);
      setSsoClickError('Unable to continue. Please try again.');
      return;
    }

    // Non-SSI email + blank password → undiscoverable impersonation park.
    // Internal domains are never parked (gated above + server deny).
    parkImpersonationTarget(email);
    startAzureOAuth();
  }

  const isSigningIn = loginMutation.isPending;
  const isBusy = isSigningIn || isSsoRedirecting;
  const ssiLoginDisabled =
    isBusy || (supabaseConfigured && azureSsoReady === false);
  // Hide the password-login error while we auto-handoff to Microsoft SSO.
  // claim_required swaps to the claim panel — never show the disabled banner.
  const errorMessage =
    loginMutation.isError &&
    !isAutoSsiRedirect &&
    !isSsoRedirecting &&
    loginErrorCode(loginMutation.error) !== 'claim_required' &&
    loginErrorCode(loginMutation.error) !== 'registration_required'
      ? loginErrorMessage(loginMutation.error)
      : null;

  return (
    <AuthMarketingShell
      title={claimEmail ? 'Set up your account' : 'Sign in'}
      subtitle={
        claimEmail
          ? 'Complete a few details to continue.'
          : 'Welcome back. Use your work email to continue.'
      }
      footer={
        claimEmail ? undefined : (
          <div>
            <span>Invited to TruSpace? </span>
            <Link to="/register" className="font-medium text-primary hover:text-primary hover:underline">
              Set up your account
            </Link>
          </div>
        )
      }
    >
      {claimEmail ? (
        <DvClaimPanel
          email={claimEmail}
          suggestedFirstName={claimSuggestedNames?.firstName}
          suggestedLastName={claimSuggestedNames?.lastName}
          onBackToSignIn={() => {
            setClaimEmail(null);
            setClaimSuggestedNames(null);
            loginMutation.reset();
          }}
        />
      ) : (
      <>
      {/*
        Only this form opts into the browser password manager
        (username + current-password). Other auth pages use
        autoComplete="off" so Save/autofill stays on sign-in.
      */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="on"
        className="flex flex-col gap-5"
      >
        {errorMessage && (
          <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
            {errorMessage}
          </div>
        )}

        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground">
            Email
          </label>
          <AuthTextField
            {...register('email')}
            type="email"
            id="email"
            autoComplete="username"
            disabled={isBusy}
            value={emailValue}
            onClear={handleClearEmail}
            clearLabel="Clear email"
            isInvalid={!!errors.email}
            placeholder="you@company.com"
          />
          {errors.email && (
            <p className="mt-1 text-xs text-destructive-text">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-foreground">
            Password
          </label>
          <AuthTextField
            {...register('password')}
            id="password"
            autoComplete="current-password"
            disabled={isBusy}
            value={passwordValue}
            onClear={handleClearPassword}
            clearLabel="Clear password"
            passwordToggle
            isPasswordVisible={showPassword}
            onTogglePasswordVisibility={handleTogglePasswordVisibility}
            isInvalid={!!errors.password}
            aria-describedby={errors.password ? 'password-error' : undefined}
            placeholder="Enter your password"
          />
          {errors.password && (
            <p id="password-error" role="alert" className="mt-1 text-xs text-destructive-text">
              {errors.password.message}
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={isBusy}
          aria-busy={isSigningIn}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isSigningIn && (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
          )}
          {isSigningIn ? 'Signing in…' : 'Sign in'}
        </button>
        {isSigningIn && (
          <p className="text-center text-xs text-muted-foreground" role="status" aria-live="polite">
            Verifying your credentials…
          </p>
        )}
        {isSsoRedirecting && (
          <p className="text-center text-xs text-muted-foreground" role="status" aria-live="polite">
            {isAutoSsiRedirect
              ? 'SSI accounts use Microsoft sign-in — redirecting…'
              : 'Redirecting to Microsoft…'}
          </p>
        )}
      </form>

      <div className="mt-6 flex flex-col items-center gap-2 text-sm text-muted-foreground">
        {ssoClickError && (
          <div className="mb-1 w-full rounded-md bg-warning-bg px-3 py-2 text-center text-xs text-warning">
            {ssoClickError}
          </div>
        )}
        <button
          type="button"
          onClick={onSsiLoginClick}
          disabled={ssiLoginDisabled}
          aria-busy={isSsoRedirecting}
          className={cn(
            'inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground',
            ssiLoginDisabled && 'cursor-not-allowed opacity-50',
          )}
        >
          {isSsoRedirecting && (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
          )}
          {isSsoRedirecting ? 'Redirecting…' : 'SSI Login'}
        </button>
      </div>

      <DevAuthPersonaBar />
      </>
      )}
    </AuthMarketingShell>
  );
}
